import type { AppIconInput } from "./appIcon";
import {
  appStoreLookupBundleId,
  appStoreTrackName,
  type AppStoreMetadata,
} from "./appStoreLookup";
import { friendlyAppName, type FriendlyAppNameInput } from "./friendlyAppName";

/** Neutral placeholder while App Store metadata is loading or resolving. */
export const APP_STORE_NAME_PLACEHOLDER = "…";

export type FriendlyAppNameResolveInput = FriendlyAppNameInput & AppIconInput;

export type ResolveFriendlyAppNameDisplayOptions = {
  isOnline: boolean;
  resolveFailed: boolean;
};

/**
 * Pure display resolver for App Store–aware friendly names. Eligible iOS bundle
 * IDs wait on cache/resolve (placeholder) before falling back to heuristics.
 */
export function resolveFriendlyAppNameDisplay(
  input: FriendlyAppNameResolveInput,
  metadata: AppStoreMetadata | null | undefined,
  { isOnline, resolveFailed }: ResolveFriendlyAppNameDisplayOptions,
): string {
  const bundleId = appStoreLookupBundleId(input);
  if (!bundleId) {
    return friendlyAppName(input);
  }

  if (metadata === undefined) {
    return APP_STORE_NAME_PLACEHOLDER;
  }

  const trackName = appStoreTrackName(metadata);
  if (trackName) return trackName;

  if (metadata?.lookupStatus === "not_found") {
    return friendlyAppName(input);
  }

  if (!isOnline || resolveFailed) {
    return friendlyAppName(input);
  }

  return APP_STORE_NAME_PLACEHOLDER;
}
